import React, { useState, useEffect, useRef, useMemo } from "react";
import { Card, Button, Spin, Breadcrumb, Layout, theme, Typography } from "antd";
import { useAuth } from "../hooks/useAuth";
import { useNavigate, useSearchParams } from "react-router-dom";
import { EyeOutlined, PieChartOutlined, CloseOutlined } from "@ant-design/icons";
import { useGetBrandsQuery, useGetProfileMutation, useGetCohortsByBrandQuery, useGetStaleCachedBrandEquityMutation } from "../services/nestjs-base-api";
import { IBrandEquityScoreResponse, ICalculateBrandEquityRequest, PeriodEnum } from "../services/nestjs-base-api/types";
import BrandEquityVisualizationWrapper from "../components/BrandEquity/BrandEquityVisualizationWrapper";
import { Modal } from "antd";
import { DataSummaryCard, useDataSummary } from "../components/BrandEquity/DataSummaryCard";
import { MaturityTag } from "../components/MaturityTag";
import { MaturityAlert } from "../components/MaturityAlert";

const { Content } = Layout;
const { Title, Text } = Typography;

const VisualizationPage: React.FunctionComponent = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const initialElement = searchParams.get('element');
    const {
        token: { colorBgContainer, borderRadiusLG },
    } = theme.useToken();

    const [results, setResults] = useState<IBrandEquityScoreResponse | null>(null);
    const [visualizationModalVisible, setVisualizationModalVisible] = useState(false);
    const [profileData, setProfileData] = useState<any>(null);
    const [loadingProfile, setLoadingProfile] = useState(false);
    const [loadingData, setLoadingData] = useState(true);
    const visualizationTypeRef = useRef<HTMLDivElement>(null);
    const hasScrolledOnMountRef = useRef(false);

    const [getProfile] = useGetProfileMutation();
    const [getStaleCachedBrandEquity] = useGetStaleCachedBrandEquityMutation();

    // Load user profile
    useEffect(() => {
        const loadProfile = async () => {
            if (!user?.jwtToken) return;

            setLoadingProfile(true);
            try {
                const result = await getProfile({ jwtToken: user.jwtToken }).unwrap();
                setProfileData(result);
            } catch (error) {
                console.error('Failed to load profile:', error);
            } finally {
                setLoadingProfile(false);
            }
        };

        loadProfile();
    }, [user?.jwtToken, getProfile]);

    // Load brands for display
    const { data: brandsData } = useGetBrandsQuery(
        {
            jwtToken: user?.jwtToken || "",
            organisationId: profileData?.organisation?.id || 0
        },
        { skip: !user?.jwtToken || !profileData?.organisation?.id }
    );

    // Get filter info to determine which brand's cohorts to load
    // Memoized to prevent creating new object reference on every render
    const filterInfo = useMemo(() => {
        if (!user?.id) return null;
        const filterKey = `brandEquityFilters_${user.id}`;
        const savedFilters = localStorage.getItem(filterKey);
        if (!savedFilters) return null;
        try {
            return JSON.parse(savedFilters);
        } catch (e) {
            return null;
        }
    }, [user?.id]);

    // Load cohorts for the selected brand
    const { data: cohortsData } = useGetCohortsByBrandQuery(
        {
            jwtToken: user?.jwtToken || "",
            organisationId: profileData?.organisation?.id || 0,
            brandId: filterInfo?.brandId || 0
        },
        { skip: !user?.jwtToken || !profileData?.organisation?.id || !filterInfo?.brandId }
    );

    // Load cached results on mount
    useEffect(() => {
        if (!user?.id) return;

        const cacheKey = `brandEquityResults_${user.id}`;
        const cached = localStorage.getItem(cacheKey);

        if (cached) {
            try {
                const { data } = JSON.parse(cached);
                setResults(data);
            } catch (e) {
                console.error('Failed to parse cached results', e);
            }
        }

        setLoadingData(false);
    }, [user?.id]);

    // Load stale cache on mount if no results in localStorage
    useEffect(() => {
        const loadStaleCache = async () => {
            if (!user?.jwtToken || !profileData?.organisation?.id || !filterInfo?.brandId || results) return;

            try {
                // Prepare filters similar to DataSelectionPage
                let payload: ICalculateBrandEquityRequest = {
                    brandIds: [filterInfo.brandId]
                };

                // Add cohort filters if specified
                if (filterInfo.cohortIds && filterInfo.cohortIds.length > 0) {
                    payload.cohortIds = filterInfo.cohortIds;
                }

                // Add country filters if specified
                if (filterInfo.countryCodes && filterInfo.countryCodes.length > 0) {
                    payload.countryCodes = filterInfo.countryCodes;
                }

                // Convert period selection to API format
                if (filterInfo.periodType && filterInfo.periodType !== 'custom') {
                    let period: PeriodEnum;
                    switch (filterInfo.periodType) {
                        case 'last30':
                            period = PeriodEnum.THIRTY_DAYS;
                            break;
                        case 'last60':
                            period = PeriodEnum.SIXTY_DAYS;
                            break;
                        case 'last90':
                            period = PeriodEnum.NINETY_DAYS;
                            break;
                        case 'last120':
                            period = PeriodEnum.ONE_TWENTY_DAYS;
                            break;
                        case 'last180':
                            period = PeriodEnum.ONE_EIGHTY_DAYS;
                            break;
                        case 'last365':
                            period = PeriodEnum.THREE_SIXTY_FIVE_DAYS;
                            break;
                        case 'allTime':
                            period = PeriodEnum.ALL_TIME;
                            break;
                        default:
                            period = PeriodEnum.THIRTY_DAYS;
                    }
                    payload.period = period;
                } else if (filterInfo.periodType === 'custom' && filterInfo.dateFrom && filterInfo.dateTo) {
                    payload.period = PeriodEnum.DATE_RANGE;
                    payload.dateFrom = filterInfo.dateFrom;
                    payload.dateTo = filterInfo.dateTo;
                } else {
                    // Default to 30 days
                    payload.period = PeriodEnum.THIRTY_DAYS;
                }

                const staleResult = await getStaleCachedBrandEquity({
                    jwtToken: user.jwtToken,
                    organisationId: profileData.organisation.id,
                    payload
                }).unwrap();

                if (staleResult) {
                    setResults(staleResult);
                    // Also save to localStorage for next time
                    const cacheKey = `brandEquityResults_${user.id}`;
                    localStorage.setItem(cacheKey, JSON.stringify({ data: staleResult }));
                }
            } catch (error) {
                // Silently fail - user can navigate to Data Selection to calculate
                console.debug('No stale cache available', error);
            }
        };

        loadStaleCache();
    }, [user?.jwtToken, profileData?.organisation?.id, filterInfo, user?.id]);

    // Smooth scroll to visualization section when page first loads with data
    useEffect(() => {
        if (!results || hasScrolledOnMountRef.current) return;

        // Function to perform the scroll
        const performScroll = () => {
            if (visualizationTypeRef.current) {
                hasScrolledOnMountRef.current = true;
                visualizationTypeRef.current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                });
            }
        };

        // Use multiple animation frames to ensure DOM is fully painted
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                // Additional timeout to ensure all content is loaded
                setTimeout(performScroll, 200);
            });
        });
    }, [results]);

    // Get data summary information using the hook
    const dataSummary = useDataSummary({
        filterInfo,
        brandsData,
        cohortsData
    });

    if (loadingProfile || loadingData) {
        return (
            <Content style={{ margin: '0 16px' }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '400px'
                }}>
                    <div style={{ textAlign: 'center' }}>
                        <Spin size="large" />
                        <div style={{ fontSize: '16px', marginTop: '16px' }}>Loading visualization...</div>
                    </div>
                </div>
            </Content>
        );
    }

    if (!results) {
        return (
            <Content style={{ margin: '0 16px' }}>
                <Breadcrumb
                    style={{ margin: '16px 0' }}
                    items={[
                        { title: user.organisation?.name || 'Organization' },
                        { title: 'Analytics' },
                        { title: 'Visualization' }
                    ]}
                />

                <div
                    style={{
                        padding: 24,
                        minHeight: '82vh',
                        background: colorBgContainer,
                        borderRadius: borderRadiusLG,
                    }}
                >
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '80px 24px',
                        textAlign: 'center',
                    }}>
                        <div style={{
                            width: 80,
                            height: 80,
                            borderRadius: '50%',
                            background: 'rgba(245, 158, 11, 0.1)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginBottom: 24,
                        }}>
                            <PieChartOutlined style={{ fontSize: 36, color: '#F59E0B' }} />
                        </div>
                        <Title level={4} style={{ marginBottom: 12 }}>No Data Available</Title>
                        <Text type="secondary" style={{ marginBottom: 24, maxWidth: 400 }}>
                            No brand equity data has been calculated yet. Go to Data Selection to calculate your brand equity.
                        </Text>
                        <Button
                            type="primary"
                            onClick={() => navigate('/dashboard/analytics/data-selection')}
                        >
                            Go to Data Selection
                        </Button>
                    </div>
                </div>
            </Content>
        );
    }

    return (
        <Content style={{ margin: '0 16px' }}>
            <Breadcrumb
                style={{ margin: '16px 0' }}
                items={[
                    { title: user.organisation?.name || 'Organization' },
                    { title: 'Analytics' },
                    { title: 'Visualization' }
                ]}
            />

            <div
                style={{
                    padding: 24,
                    minHeight: '82vh',
                    background: colorBgContainer,
                    borderRadius: borderRadiusLG,
                }}
            >
                {/* Header */}
                <div style={{ marginBottom: '24px' }}>
                    <Title level={2} style={{ margin: 0, marginBottom: '8px' }}>
                        Brand Equity Visualization
                        <MaturityTag type="beta" />
                    </Title>
                    <Text style={{ color: '#6b7280', fontSize: '16px' }}>
                        Visual representation of your brand equity flow and analysis
                    </Text>
                    <MaturityAlert type="beta" style={{ marginTop: 16 }} />
                </div>

                {/* Data Summary */}
                <DataSummaryCard
                    brandName={dataSummary.brandName}
                    periodDescription={dataSummary.periodDescription}
                    cohortNames={dataSummary.cohortNames}
                    countryNames={dataSummary.countryNames}
                    totalResponses={results.totalResponses}
                    calculatedAt={results.calculatedAt}
                    changeFiltersLink="/dashboard/analytics/data-selection"
                />

                {/* Visualization */}
                <div ref={visualizationTypeRef} />
                <Card
                    title="Brand Equity Flow"
                    style={{
                        borderRadius: '16px',
                        marginBottom: '24px'
                    }}
                    extra={
                        <Button
                            icon={<EyeOutlined />}
                            onClick={() => setVisualizationModalVisible(true)}
                            size="small"
                        >
                            Expand
                        </Button>
                    }
                >
                    <div style={{
                        height: '1000px',
                        width: '100%',
                        position: 'relative'
                    }}>
                        <BrandEquityVisualizationWrapper
                            results={results}
                            isModal={false}
                            isAdmin={false}
                            organisationId={profileData?.organisation?.id}
                            brandId={filterInfo?.brandId}
                            forceVisualizationType="orbital-v3"
                            initialSelectedElement={initialElement}
                        />
                    </div>
                </Card>
            </div>

            {/* Full Page Visualization Modal */}
            <style>{`
                .fullscreen-visualization-modal .ant-modal-wrap {
                    overflow: hidden !important;
                }
                .fullscreen-visualization-modal .ant-modal {
                    overflow: hidden !important;
                    max-width: 100vw !important;
                    padding: 0 !important;
                }
            `}</style>
            <Modal
                title={null}
                open={visualizationModalVisible}
                onCancel={() => setVisualizationModalVisible(false)}
                footer={null}
                width="100vw"
                closable={true}
                closeIcon={
                    <CloseOutlined style={{
                        fontSize: 18,
                        padding: 8,
                        borderRadius: 6,
                        backgroundColor: 'rgba(0, 0, 0, 0.6)',
                        color: '#ffffff',
                        transition: 'background-color 0.2s'
                    }} />
                }
                style={{
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    margin: 0,
                    maxWidth: 'none'
                }}
                styles={{
                    body: {
                        height: '100vh',
                        padding: 0,
                        overflow: 'hidden'
                    },
                    content: {
                        height: '100vh',
                        borderRadius: 0,
                        padding: 0,
                        overflow: 'hidden'
                    },
                    header: {
                        position: 'absolute',
                        top: 16,
                        right: 16,
                        zIndex: 1000,
                        background: 'transparent',
                        border: 'none',
                        padding: 0,
                        margin: 0
                    },
                    mask: {
                        backgroundColor: 'rgba(0, 0, 0, 0.8)'
                    }
                }}
                centered={false}
                rootClassName="fullscreen-visualization-modal"
            >
                {results && (
                    <div style={{
                        height: '100%',
                        width: '100%',
                        overflow: 'hidden'
                    }}>
                        <BrandEquityVisualizationWrapper
                            results={results}
                            isModal={true}
                            isAdmin={false}
                            organisationId={profileData?.organisation?.id}
                            brandId={filterInfo?.brandId}
                            forceVisualizationType="orbital-v3"
                        />
                    </div>
                )}
            </Modal>
        </Content>
    );
};

export default VisualizationPage;
